import {
  deckSizeKey,
  deckSizeLabel,
  DECK_SIZES,
  MAX_PARTY_PLAYERS,
  STARFALL_EVENT_KINDS,
  WILD_KINDS,
  type AIDifficulty,
  type DeckSize,
} from "@fiveout/core";
import { statTotal, type PlayerProfile } from "./profile";

const AI_DIFFICULTIES: readonly AIDifficulty[] = ["easy", "medium", "hard", "insane"];
const AI_LEVEL_NAMES: Record<AIDifficulty, string> = { easy: "Easy", medium: "Medium", hard: "Hard", insane: "Insane" };

/**
 * The achievement catalog.
 *
 * Tiered families (rounds played, Five Outs, and so on) are generated rather
 * than hand-written, and the achievements UI collapses each family to a single
 * row showing the tier you're on and the next one — otherwise the 13-tier
 * families would bury everything else.
 *
 * Rules baked in here (see IMPLEMENTATION_PLAN.md §5):
 *   - "A game" means a ROUND, never a match.
 *   - Solo rounds never count as wins (a lone player always empties their deck
 *     eventually), so they're graded on turn count and Five Outs instead.
 *   - AI achievements are 1v1 only: exactly one human against exactly one AI.
 *   - Nothing cosmetic is gated behind a top-tier achievement.
 */

export type AchievementCategory =
  | "rounds"
  | "wins"
  | "solo"
  | "matches"
  | "wilds"
  | "stars"
  | "flair"
  // Earned in the campaign alone, and listed apart from everything else:
  // campaign rounds never count toward the regular stats these read.
  | "campaign";

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  /** Tiered achievements share a family id so the UI can collapse them. */
  family?: string;
  /** Position within the family, 1-based. */
  tier?: number;
  /** Target value for progress display. */
  target: number;
  /** Current progress toward `target`, read from the profile. */
  progress: (p: PlayerProfile) => number;
  /**
   * A secret: until it is earned the achievement list shows "???" in its place
   * (`buildAchievementView`). It still counts in every total.
   */
  hidden?: boolean;
}

function tiered(opts: {
  family: string;
  category: AchievementCategory;
  thresholds: number[];
  name: (n: number) => string;
  description: (n: number) => string;
  progress: (p: PlayerProfile) => number;
}): Achievement[] {
  return opts.thresholds.map((n, i) => ({
    id: `${opts.family}.${n}`,
    name: opts.name(n),
    description: opts.description(n),
    category: opts.category,
    family: opts.family,
    tier: i + 1,
    target: n,
    progress: opts.progress,
  }));
}

function single(opts: {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  progress: (p: PlayerProfile) => number;
  target?: number;
  hidden?: boolean;
}): Achievement {
  return { ...opts, target: opts.target ?? 1 };
}

/**
 * Every tier is a real milestone. The first tiers used to be 1 (and families
 * like Five Outs, wilds and stars started at 1 too), so a single solo half-deck
 * round unlocked eight achievements and landed a new player on level 4. Each
 * tier is now strictly higher than the one it replaced.
 */
const COUNT_TIERS = [5, 10, 25, 50, 75, 100, 150, 250, 400, 500, 750, 1000, 1500];

/**
 * Cards out of the deck in one turn that counts as a big turn.
 *
 * A Five Out alone is five — the played card and its four bonuses — so seven
 * needs that turn to have compounded: a chained duplicate, a wild firing and
 * taking its follow-up, a pile taken and refilled. Reachable, but only when
 * several things land at once, which is what makes it worth a badge.
 */
const BIG_TURN_CARDS = 7;

/**
 * Untouchable (Untouched's diamond) wants a round dealt at least this deep.
 * A half deck split four ways is a handful of cards, and winning that without
 * a take is no feat; how many seats there are says nothing on its own. The
 * counter keeps its old `bigTableFlawlessWins` name, since profiles hold it.
 */
export const UNTOUCHABLE_MIN_DEALT = 20;

/**
 * Lobby sizes the online win achievements ask about.
 *
 * `exact` for the one-on-one, because it is its own thing and winning an
 * eight-hander is not doing it; `atLeast` for the rest, which are milestones on
 * the way up. Forty is the party cap (MAX_PARTY_PLAYERS), so "at least" and
 * "exactly" are the same question there — `atLeast` says why the number is 40.
 */
const ONLINE_WIN_SIZES: { id: string; name: string; description: string; size: number; exact?: boolean }[] = [
  {
    id: "online.win.1v1",
    name: "Head to Head",
    size: 2,
    exact: true,
    description: "Win an online round one-on-one.",
  },
  {
    id: "online.win.4",
    name: "Four at the Table",
    size: 4,
    description: "Win an online round in a lobby of 4 or more.",
  },
  {
    id: "online.win.8",
    name: "Eight at the Table",
    size: 8,
    description: "Win an online round in a lobby of 8 or more.",
  },
  {
    id: "online.win.full",
    name: "Full House",
    size: MAX_PARTY_PLAYERS,
    description: `Win an online round in a full party of ${MAX_PARTY_PLAYERS}.`,
  },
];

/**
 * The online win achievements: one per lobby size, reading the set of sizes the
 * player has won at.
 *
 * Singles rather than a tiered family because they are not a ladder — the
 * one-on-one asks an exact question the others do not, so collapsing them to
 * "the biggest you have won" would hand out the head-to-head badge to anybody
 * who won a bigger table first.
 */
function onlineWinAchievements(): Achievement[] {
  return ONLINE_WIN_SIZES.map((spec) =>
    single({
      id: spec.id,
      name: spec.name,
      description: spec.description,
      category: "matches",
      progress: (p) => {
        const sizes = Object.keys(p.stats.onlineWinTableSizes ?? {}).map(Number);
        const hit = spec.exact ? sizes.includes(spec.size) : sizes.some((n) => n >= spec.size);
        return hit ? 1 : 0;
      },
    }),
  );
}

/**
 * Solo turn-count targets, per deck size. PROVISIONAL — solo play is a long,
 * strategic mode (a 2-deck solo round can run 30-40 minutes) and the AI used by
 * scripts/calibrate-solo.mjs doesn't exploit Match guesses or stack-order
 * memorisation the way a human does, so simulation under-reports what's
 * achievable. Re-run that script, or better, tune these from the real turn-count
 * high-score tables once there's play data.
 *
 * Only the half deck and the full deck have turn-count tiers. At 1.5 decks and
 * up, simply completing the round is the achievement.
 */
export const SOLO_TURN_TIERS: Partial<Record<string, number[]>> = {
  // A half deck deals its solo player 22 cards, so the floor is not far under
  // that: the only way below it is turns that lay several cards at once. 12 was
  // simulation output, and simulation was wrong — it is not reachable. 18 is the
  // real target, with the middle tier moved out to keep the three evenly spaced
  // rather than pinning two of them within two turns of each other.
  "0.5": [26, 22, 18],
  "1": [110, 75, 55],
};

const TURN_TIER_NAMES = ["Steady", "Sharp", "Surgical"];

function soloTurnAchievements(): Achievement[] {
  const out: Achievement[] = [];
  for (const size of DECK_SIZES) {
    const key = deckSizeKey(size);
    const tiers = SOLO_TURN_TIERS[key];
    if (!tiers) continue;
    tiers.forEach((turns, i) => {
      out.push({
        id: `solo.turns.${key}.${turns}`,
        name: `${TURN_TIER_NAMES[i]} — ${deckSizeLabel(size)}`,
        description: `Finish a solo ${deckSizeLabel(size).toLowerCase()} round in ${turns} turns or fewer.`,
        category: "solo",
        family: `solo.turns.${key}`,
        tier: i + 1,
        target: turns,
        // Progress is inverted (lower is better), so the UI reads `target` and
        // `progress` as "your best" rather than "how far along".
        progress: (p) => p.soloScores[key]?.[0]?.turns ?? 0,
      });
    });
  }
  return out;
}

function soloCompletionAchievements(): Achievement[] {
  return DECK_SIZES.map((size) => {
    const key = deckSizeKey(size);
    return single({
      id: `solo.complete.${key}`,
      name: `Solo: ${deckSizeLabel(size)}`,
      description: `Empty your deck in a solo ${deckSizeLabel(size).toLowerCase()} round.`,
      category: "solo",
      progress: (p) => (p.stats.soloDeckSizesCompleted[key] ? 1 : 0),
    });
  });
}

/** What each tier of the destroyed-in-one-turn family is called. */
const WRECK_NAMES: Record<number, string> = { 10: "Wrecking Ball", 20: "Bulldozer", 35: "Scorched Earth", 50: "Clean Slate" };

/** What each tier of Impact is called: the most cards one meteor event took off the table. */
const IMPACT_NAMES: Record<number, string> = { 10: "Impact", 18: "Crater", 26: "Cataclysm", 35: "Extinction Event" };

/**
 * Constellations. Every tally counts what the PLAYER did - a star they lit, an
 * event their star fired - never what the table did around them, except Full
 * Sky, which is about having seen the whole event deck.
 */
function starAchievements(): Achievement[] {
  return [
    ...tiered({
      family: "stars.lit",
      category: "stars",
      thresholds: [15, 100, 400, 1000],
      name: (n) => (n === 15 ? "Stargazer" : `${n} Stars`),
      description: (n) => `Light ${n} stars on the Star Meter.`,
      progress: (p) => statTotal(p, "starsLit"),
    }),
    ...tiered({
      family: "stars.quad",
      category: "stars",
      thresholds: [3, 25, 100, 300],
      name: (n) => (n === 3 ? "Four of a Kind" : `${n} Fours of a Kind`),
      description: (n) => `Light ${n === 1 ? "a star" : `${n} stars`} with four of a kind.`,
      progress: (p) => statTotal(p, "quadStars"),
    }),
    ...tiered({
      family: "stars.stair",
      category: "stars",
      thresholds: [5, 40, 150, 400],
      name: (n) => (n === 5 ? "Stairway to the Stars" : `${n} Staircases`),
      description: (n) => `Light ${n === 1 ? "a star" : `${n} stars`} with a staircase.`,
      progress: (p) => statTotal(p, "staircaseStars"),
    }),
    ...tiered({
      family: "stars.events",
      category: "stars",
      thresholds: [3, 25, 100, 250],
      name: (n) => (n === 3 ? "Wish Upon a Star" : `${n} Wishes`),
      description: (n) => `Light the third star and fire ${n === 1 ? "an event" : `${n} events`}.`,
      progress: (p) => statTotal(p, "eventsTriggered"),
    }),
    ...tiered({
      family: "bounty.claims",
      category: "stars",
      thresholds: [5, 25, 100, 250],
      name: (n) => (n === 5 ? "Bounty Hunter" : `${n} Bounties`),
      description: (n) => `Claim ${n === 1 ? "a free card" : `${n} free cards`} from a Bounty Pile.`,
      progress: (p) => statTotal(p, "bountyClaims"),
    }),
    ...tiered({
      family: "bounty.fiveout",
      category: "stars",
      // Rare by construction: one lap, two piles, and it has to be exact.
      thresholds: [2, 5, 15, 40],
      name: (n) => (n === 2 ? "Jackpot" : `${n} Jackpots`),
      description: (n) => `Hit ${n === 1 ? "a Five Out" : `${n} Five Outs`} on a Bounty Pile.`,
      progress: (p) => statTotal(p, "bountyFiveOuts"),
    }),
    ...tiered({
      family: "bounty.offbyone",
      category: "stars",
      thresholds: [3, 20, 60, 150],
      name: (n) => (n === 3 ? "Windfall" : `${n} Windfalls`),
      description: (n) => `Land ${n === 1 ? "an off-by-one match" : `${n} off-by-one matches`} on a Bounty Pile.`,
      progress: (p) => statTotal(p, "bountyOffByOnes"),
    }),
    ...tiered({
      family: "meteor.destroyed",
      category: "stars",
      // A best, not a total. A Swarm alone can reach 7, so even the first tier
      // takes a Meteor Strike's big one landing on a tall pile.
      thresholds: [10, 18, 26, 35],
      name: (n) => IMPACT_NAMES[n] ?? `Impact ${n}`,
      description: (n) => `Destroy ${n} or more cards with one meteor event your star fired.`,
      progress: (p) => p.stats.bestMeteorDestroyed,
    }),
    single({
      id: "stars.nightsky",
      name: "Night Sky",
      description: "Light 3 stars in a single round.",
      category: "stars",
      target: 3,
      progress: (p) => p.stats.bestStarsInRound,
    }),
    single({
      id: "stars.double",
      name: "Double Feature",
      description: "Light 2 stars with a single card.",
      category: "stars",
      target: 2,
      progress: (p) => p.stats.bestStarsInOneCard,
    }),
    single({
      id: "stars.fullsky",
      name: "Full Sky",
      description: "See every Constellations event fire.",
      category: "stars",
      // Read off the event list, so a new event raises the bar for everybody.
      target: STARFALL_EVENT_KINDS.length,
      progress: (p) => STARFALL_EVENT_KINDS.filter((k) => p.stats.starfallEventsSeen[k]).length,
    }),
    single({
      id: "stars.collision",
      name: "Cosmic Collision",
      description: "Fire an event on the turn you go out.",
      category: "stars",
      progress: (p) => statTotal(p, "cosmicCollisions"),
    }),
    single({
      id: "stars.comet",
      name: "Written in the Stars",
      description: "Go out on a Comet Shower.",
      category: "stars",
      progress: (p) => statTotal(p, "cometGoOuts"),
    }),
    single({
      id: "stars.weather",
      name: "Weathered the Storm",
      description: "Win a round in which a Card Storm or meteors hit the table.",
      category: "stars",
      progress: (p) => statTotal(p, "stormWins"),
    }),
    single({
      id: "stars.starless",
      name: "Starless",
      description: "Win a round with Constellations on without lighting a star.",
      category: "stars",
      progress: (p) => statTotal(p, "starlessWins"),
    }),
    single({
      id: "stars.longgame",
      name: "The Long Game",
      description: "Win a half-deck round with Constellations on, having lit a star.",
      category: "stars",
      progress: (p) => statTotal(p, "halfDeckStarWins"),
    }),
  ];
}

export const ACHIEVEMENTS: Achievement[] = [
  ...tiered({
    family: "rounds.played",
    category: "rounds",
    thresholds: COUNT_TIERS,
    name: (n) => (n === COUNT_TIERS[0] ? "Regular" : `${n} Rounds`),
    description: (n) => `Play ${n} round${n === 1 ? "" : "s"}.`,
    progress: (p) => statTotal(p, "roundsPlayed"),
  }),
  ...tiered({
    family: "rounds.won",
    category: "wins",
    thresholds: COUNT_TIERS,
    name: (n) => (n === COUNT_TIERS[0] ? "First Blood" : `${n} Wins`),
    description: (n) => `Win ${n} round${n === 1 ? "" : "s"} against other players or AI.`,
    progress: (p) => statTotal(p, "roundsWon"),
  }),

  // One per CPU level, and together the CPU Slayer badge: plastic for Easy up to
  // diamond for Insane. The ids predate the badge and are kept, because XP is
  // derived from the achievements held.
  ...AI_DIFFICULTIES.map((level) =>
    single({
      id: `ai.${level}`,
      name: `CPU Slayer: ${AI_LEVEL_NAMES[level]}`,
      description: `Beat the ${AI_LEVEL_NAMES[level]} CPU in a one-on-one round.`,
      category: "wins",
      progress: (p) => (p.stats.aiDefeated[level] ? 1 : 0),
    }),
  ),

  ...soloCompletionAchievements(),
  ...soloTurnAchievements(),

  ...tiered({
    family: "fiveout.round",
    category: "flair",
    thresholds: [3, 5, 7, 10, 12],
    name: (n) => `${n} in a Round`,
    description: (n) => `Hit ${n} Five Outs in a single round.`,
    progress: (p) => p.stats.bestFiveOutsInRound,
  }),
  ...tiered({
    family: "fiveout.total",
    category: "flair",
    thresholds: [10, 25, 100, 250, 500, 1000],
    name: (n) => (n === 10 ? "Five Out!" : `${n} Five Outs`),
    description: (n) => `Hit ${n} Five Out${n === 1 ? "" : "s"} in total.`,
    progress: (p) => statTotal(p, "fiveOuts"),
  }),
  ...tiered({
    family: "offbyone.round",
    category: "flair",
    thresholds: [5, 8, 12, 16],
    name: (n) => `${n} Near Misses`,
    description: (n) => `Land ${n} off-by-one matches in a single round.`,
    progress: (p) => p.stats.bestOffByOnesInRound,
  }),
  ...tiered({
    family: "offbyone.total",
    category: "flair",
    thresholds: [25, 100, 300, 750],
    name: (n) => `${n} Bonus Pairs`,
    description: (n) => `Land ${n} off-by-one matches in total.`,
    progress: (p) => statTotal(p, "offByOnes"),
  }),
  ...tiered({
    family: "chain",
    category: "flair",
    // A chain is every card the player lays on ONE pile in a row in one turn -
    // the played card, duplicates, +/-1 bonuses, a wild and the card laid back
    // over it - not only the chaining bonus (recorder.ts, `chainRun`). Counted
    // that way 7 stops being a one-in-a-million run of +1 draws and becomes a
    // turn where several rules compound on one pile. The ids of 2 and 3 are
    // kept: XP is derived from the achievements held, and retiring them would
    // take levels away from the players who already have them.
    thresholds: [2, 3, 7],
    name: (n) => `Chain of ${n}`,
    description: (n) => `Lay ${n} cards in a row on one pile in a single turn. Wilds count.`,
    progress: (p) => p.stats.longestChain,
  }),
  single({
    id: "flair.bigturn",
    name: "Avalanche",
    description: "Lay 7 or more cards out of your deck in a single turn.",
    category: "flair",
    target: BIG_TURN_CARDS,
    progress: (p) => p.stats.bestCardsInTurn,
  }),

  ...tiered({
    family: "wilds.played",
    category: "wilds",
    thresholds: [10, 25, 100, 250, 500],
    name: (n) => (n === 10 ? "Gone Wild" : `${n} Wilds`),
    description: (n) => `Play ${n} wild card${n === 1 ? "" : "s"}.`,
    progress: (p) => statTotal(p, "wildsPlayed"),
  }),
  ...tiered({
    family: "wilds.bigsmash",
    category: "wilds",
    thresholds: [3, 15, 40, 100],
    name: (n) => (n === 3 ? "Demolition" : `${n} Demolitions`),
    description: (n) => `Smash a pile of 8 or more cards ${n} time${n === 1 ? "" : "s"}.`,
    progress: (p) => statTotal(p, "bigSmashes"),
  }),
  // The Unicorn badge's one tier, and it is diamond: a deck holds only one of
  // each kind per full unit, so all four in a turn needs wilds turning up as
  // the cards laid back over wilds. Every kind, not every kind the table has
  // on - a table with Peek switched off cannot give it.
  single({
    id: "wilds.unicorn",
    name: "Unicorn",
    description: "Lay a Smash, a Lightning, a Swap and a Peek in a single turn.",
    category: "wilds",
    target: WILD_KINDS.length,
    progress: (p) => p.stats.bestWildKindsInTurn,
  }),
  // The Alicorn frame's trigger, not a badge's: it has no tiers of its own to
  // show, so it is one threshold and the frame is what it unlocks. Rare on
  // purpose - one event a turn is the norm.
  single({
    id: "stars.alicorn",
    name: "Alicorn",
    description: "Set off two Constellations events in a single turn.",
    category: "stars",
    target: 2,
    progress: (p) => p.stats.bestEventsInTurn,
  }),

  ...tiered({
    family: "match.streak",
    category: "matches",
    thresholds: [3, 5, 8, 12],
    name: (n) => `${n} in a Row`,
    description: (n) => `Win ${n} rounds back to back.`,
    progress: (p) => p.stats.bestWinStreak,
  }),
  ...onlineWinAchievements(),

  ...tiered({
    family: "table.size",
    category: "matches",
    thresholds: [3, 5, 8, 12],
    name: (n) => `Table for ${n}`,
    description: (n) => `Play a round with ${n} players at the table.`,
    // Any recorded player count at or above the target satisfies the tier.
    progress: (p) =>
      Object.keys(p.stats.playerCountsPlayed).reduce((best, k) => Math.max(best, Number(k)), 0),
  }),

  ...tiered({
    family: "destroy.turn",
    category: "flair",
    // A best, not a total: Smash, Lightning and the meteors a star of yours
    // fires, all in the one turn.
    thresholds: [10, 20, 35, 50],
    name: (n) => WRECK_NAMES[n] ?? `Wreck ${n}`,
    description: (n) => `Destroy ${n} cards in a single turn with Smash, Lightning and meteors.`,
    progress: (p) => p.stats.bestDestroyedInTurn,
  }),
  ...tiered({
    family: "locks.round",
    category: "matches",
    // Padlocks sent, not players marked: a second mark on somebody sends one.
    thresholds: [2, 4, 8, 16],
    name: (n) => (n === 2 ? "Locksmith" : `${n} Locks in a Round`),
    description: (n) => `Lock ${n} of other players' piles with your streaks in a single online round.`,
    progress: (p) => p.stats.bestLocksInRound,
  }),
  ...tiered({
    family: "fire.alight",
    category: "flair",
    // Turns PAST the five that light the deck (`STREAK_TURNS_TO_IGNITE`), so 2
    // means seven clean turns in a row. Kept low on purpose: staying alight
    // is hard and leans on luck. Online only, like the lock figures.
    thresholds: [2, 4, 8, 10],
    name: (n) => (n === 2 ? "Slow Burn" : `${n} Turns Alight`),
    description: (n) => `In an online round, keep your deck alight for ${n} turns without spending it.`,
    progress: (p) => p.stats.bestTurnsAlightInRound,
  }),
  // Locks only ever go 0, 2, 3, 4 (`locksAfterTargeting`), so a spend that
  // leaves a player on three added the third, and one on four the fourth.
  ...tiered({
    family: "locks.third",
    category: "matches",
    thresholds: [5, 15, 40, 80],
    name: (n) => (n === 5 ? "Piling On" : `${n} Third Locks`),
    description: (n) => `In online rounds, add a third lock to a player who already has two, ${n} times.`,
    progress: (p) => statTotal(p, "thirdLocks"),
  }),
  ...tiered({
    family: "locks.fourth",
    category: "matches",
    thresholds: [1, 5, 10, 20],
    name: (n) => (n === 1 ? "Dead Bolt" : `${n} Dead Bolts`),
    description: (n) =>
      n === 1
        ? "In an online round, add a fourth lock to a player who already has three."
        : `In online rounds, add a fourth lock to a player who already has three, ${n} times.`,
    progress: (p) => statTotal(p, "fourthLocks"),
  }),
  ...tiered({
    family: "mercy.burst",
    category: "flair",
    thresholds: [3, 6, 9, 12],
    name: (n) => (n === 3 ? "Baby Binky" : `${n} Binkies`),
    description: (n) => `In online rounds, be handed a Mercy burst ${n} times.`,
    progress: (p) => statTotal(p, "mercyBursts"),
  }),

  single({
    id: "flair.flawless",
    name: "Untouched",
    description: "Win a round without ever taking a pile.",
    category: "flair",
    progress: (p) => statTotal(p, "flawlessWins"),
  }),
  single({
    id: "flair.flawless.online4",
    name: "Untouchable",
    description: `Win an online round you were dealt ${UNTOUCHABLE_MIN_DEALT} or more cards in without ever taking a pile.`,
    category: "flair",
    progress: (p) => statTotal(p, "bigTableFlawlessWins"),
  }),
  single({
    id: "flair.comeback",
    name: "From the Back",
    description: "Win a round while sitting last on the match table.",
    category: "matches",
    progress: (p) => statTotal(p, "comebackWins"),
  }),
  single({
    id: "flair.allvenues",
    name: "Well Traveled",
    description: "Play a round in every venue.",
    category: "flair",
    target: 6,
    progress: (p) => Object.keys(p.stats.venuesPlayed).length,
  }),
  single({
    id: "flair.alldecksizes",
    name: "Every Shape of Deck",
    description: "Play a round at every deck size.",
    category: "flair",
    target: DECK_SIZES.length,
    progress: (p) => Object.keys(p.stats.deckSizesPlayed).length,
  }),
  ...tiered({
    family: "fiveout.goout",
    category: "flair",
    thresholds: [3, 10, 30, 75],
    name: (n) => (n === 3 ? "Five and Out" : `${n} Five and Outs`),
    description: (n) => (n === 1 ? "Go out on a Five Out." : `Go out on a Five Out ${n} times.`),
    progress: (p) => statTotal(p, "fiveOutGoOuts"),
  }),
  ...tiered({
    family: "swap.trap",
    category: "wilds",
    // Only when the card you swapped there is still on top as their turn
    // begins: a storm or a meteor in between did the work, not you.
    thresholds: [3, 20, 60, 150],
    name: (n) => (n === 3 ? "Switcheroo" : `${n} Switcheroos`),
    description: (n) =>
      `Swap a card onto a pile and have the next player take that pile on it${n === 1 ? "" : `, ${n} times`}.`,
    progress: (p) => statTotal(p, "swapTraps"),
  }),

  ...starAchievements(),

  single({
    id: "flair.firstfiveout",
    name: "Perfect Match",
    description: "Land your very first Five Out.",
    category: "flair",
    progress: (p) => Math.min(1, statTotal(p, "fiveOuts")),
  }),

  // The campaign's own, read off `profile.campaign` (each level's first clear)
  // and never off the counters, which campaign rounds do not touch. One per
  // chapter, for clearing its boss: that is how a boss pays more than the flat
  // per-level XP without the game server having to know the level list.
  single({
    id: "campaign.chapter.1",
    name: "Shore Leave",
    description: "Clear the first chapter of the campaign.",
    category: "campaign",
    // The chapter-1 boss level's id in campaign/levels.ts.
    progress: (p) => (p.campaign["c1.perfect-match"] ? 1 : 0),
  }),
  single({
    id: "campaign.chapter.2",
    name: "High Country",
    description: "Clear the second chapter of the campaign.",
    category: "campaign",
    // The chapter-2 boss level's id in campaign/levels.ts.
    progress: (p) => (p.campaign["c2.whiteout"] ? 1 : 0),
  }),
  single({
    id: "campaign.chapter.3",
    name: "Face to Face",
    description: "Clear the third chapter of the campaign.",
    category: "campaign",
    // The chapter-3 boss level's id in campaign/levels.ts.
    progress: (p) => (p.campaign["c3.checkmate"] ? 1 : 0),
  }),
  single({
    id: "campaign.chapter.4",
    name: "Under Lock and Key",
    description: "Clear the fourth chapter of the campaign.",
    category: "campaign",
    // The chapter-4 boss level's id in campaign/levels.ts.
    progress: (p) => (p.campaign["c4.vault"] ? 1 : 0),
  }),
  single({
    id: "campaign.chapter.5",
    name: "Star Chart",
    description: "Clear the fifth chapter of the campaign.",
    category: "campaign",
    // The chapter-5 boss level's id in campaign/levels.ts.
    progress: (p) => (p.campaign["c5.zenith"] ? 1 : 0),
  }),
  // Zenith's bonus tiers - the Starstone badge's silver, gold and diamond rims
  // - read the best count of bonus objectives met in any cleared round
  // (`campaignBest`, set by recordCampaignClear), so they stack: a first clear
  // with two bonuses earns the clear, sapphire and topaz together.
  single({
    id: "campaign.zenith.sapphire",
    name: "Sapphire Sky",
    description: "Clear Zenith with at least one of its bonus objectives met.",
    category: "campaign",
    progress: (p) => ((p.campaignBest["c5.zenith"] ?? 0) >= 1 ? 1 : 0),
  }),
  single({
    id: "campaign.zenith.topaz",
    name: "Topaz Sky",
    description: "Clear Zenith with at least two of its bonus objectives met.",
    category: "campaign",
    progress: (p) => ((p.campaignBest["c5.zenith"] ?? 0) >= 2 ? 1 : 0),
  }),
  single({
    id: "campaign.zenith.diamond",
    name: "Diamond Sky",
    description: "Clear Zenith with all three of its bonus objectives met.",
    category: "campaign",
    progress: (p) => ((p.campaignBest["c5.zenith"] ?? 0) >= 3 ? 1 : 0),
  }),
  // A secret level's reward: the list shows "???" until it is earned, and the
  // description does not say where the level is.
  single({
    id: "campaign.polaris",
    name: "True North",
    description: "Clear a secret level of the campaign.",
    category: "campaign",
    hidden: true,
    progress: (p) => (p.campaign["c5.polaris"] ? 1 : 0),
  }),
];

export const ACHIEVEMENTS_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

/** Total number of achievements in the catalog — the denominator for levelling. */
export const TOTAL_ACHIEVEMENTS = ACHIEVEMENTS.length;

/** True when the profile currently satisfies this achievement's condition. */
export function isSatisfied(a: Achievement, profile: PlayerProfile): boolean {
  // Solo turn-count tiers are "lower is better" and only count once a run exists.
  if (a.family?.startsWith("solo.turns.")) {
    const best = a.progress(profile);
    return best > 0 && best <= a.target;
  }
  return a.progress(profile) >= a.target;
}

/**
 * Evaluates the whole catalog and unlocks anything newly satisfied. Returns the
 * achievements that were unlocked by this call, for the toast queue.
 *
 * No XP is paid out here: it is derived from the counters and the achievement
 * count, and updateProfile recomputes it once the mutation is done (§B2). Call
 * sites still itemise the payout for the post-round summary.
 */
export function evaluateAchievements(profile: PlayerProfile): Achievement[] {
  const unlocked: Achievement[] = [];
  const now = Date.now();
  for (const a of ACHIEVEMENTS) {
    if (profile.achievements[a.id]) continue;
    if (isSatisfied(a, profile)) {
      profile.achievements[a.id] = now;
      unlocked.push(a);
    }
  }
  return unlocked;
}

export const HIDDEN_NAME = "???";
export const HIDDEN_DESCRIPTION = "A secret of the campaign";

/** A standalone achievement as the list shows it: `name` and `description` are what to print. */
export interface SingleView {
  achievement: Achievement;
  unlocked: boolean;
  name: string;
  description: string;
}

export interface FamilyView {
  family: string;
  category: AchievementCategory;
  /** The highest tier already unlocked, if any. */
  current: Achievement | null;
  /** The next tier to chase, or null when the family is complete. */
  next: Achievement | null;
  unlockedCount: number;
  totalCount: number;
}

/**
 * Collapses the catalog for display: one row per tiered family (current tier +
 * next tier only) plus every standalone achievement. This is what keeps the
 * 13-tier round counters from drowning out everything more interesting.
 */
export function buildAchievementView(profile: PlayerProfile): {
  families: FamilyView[];
  singles: SingleView[];
} {
  const families = new Map<string, Achievement[]>();
  const singles: SingleView[] = [];

  for (const a of ACHIEVEMENTS) {
    if (!a.family) {
      const unlocked = !!profile.achievements[a.id];
      // A secret still unearned reads "???" instead of its own words. It stays
      // in the view, so the totals still count it.
      const masked = a.hidden && !unlocked;
      singles.push({
        achievement: a,
        unlocked,
        name: masked ? HIDDEN_NAME : a.name,
        description: masked ? HIDDEN_DESCRIPTION : a.description,
      });
      continue;
    }
    const list = families.get(a.family) ?? [];
    list.push(a);
    families.set(a.family, list);
  }

  const familyViews: FamilyView[] = [];
  for (const [family, tiers] of families) {
    const sorted = [...tiers].sort((x, y) => (x.tier ?? 0) - (y.tier ?? 0));
    const unlockedTiers = sorted.filter((t) => profile.achievements[t.id]);
    const current = unlockedTiers.length ? unlockedTiers[unlockedTiers.length - 1] : null;
    const next = sorted.find((t) => !profile.achievements[t.id]) ?? null;
    familyViews.push({
      family,
      category: sorted[0].category,
      current,
      next,
      unlockedCount: unlockedTiers.length,
      totalCount: sorted.length,
    });
  }

  return { families: familyViews, singles };
}

/** How many achievements the player has unlocked. Drives the level curve. */
export function unlockedCount(profile: PlayerProfile): number {
  return Object.keys(profile.achievements).filter((id) => ACHIEVEMENTS_BY_ID.has(id)).length;
}

export type { DeckSize };
